import { GOALS } from '@/lib/goals';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { RegenLogo } from '@/components/RegenLogo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, Loader2, Check } from 'lucide-react';

const LOSE_PREFS = ['Conservative', 'Moderate (Recommended)', 'Aggressive'];
const MUSCLE_PREFS = ['Lean Muscle Gain', 'Maximum Muscle Gain'];
const FITNESS = ['Beginner', 'Intermediate', 'Advanced'];
const DAYS = [1, 2, 3, 4, 5, 6];
const LOCATIONS = ['Gym', 'Home'];
const STYLES = ['Gym', 'HIIT', 'Both'];
const RACING = ['Yes', 'No'];
const EATING = ['Standard', 'Intermittent Fasting'];
const DIETARY = ['No Preference', 'High Protein', 'Vegetarian', 'Pescatarian', 'Vegan', 'Halal', 'Gluten Free', 'Dairy Free'];

const isLose = (g) => g === 'Lose Fat';
const isMuscle = (g) => g === 'Build Muscle';

export default function TrackerOnboarding() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: user?.name || '', age: '', gender: '', height_cm: '', current_weight_kg: '', target_weight_kg: '',
    goal: '', goal_preference: '',
    fitness_level: '', training_days: '', training_location: '', training_style: '',
    eating_style: '', dietary_preference: 'No Preference', step_goal: 8000,
    fitness_racing_interest: '',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const needsPref = isLose(f.goal) || isMuscle(f.goal);

  const steps = [
    {
      title: 'About You', valid: () => f.name && f.age && f.gender && f.height_cm && f.current_weight_kg,
    },
    {
      title: 'Your Goal', valid: () => f.goal && (!needsPref || f.goal_preference),
    },
    {
      title: 'Your Training', valid: () => f.fitness_level && f.training_days && f.training_location && f.training_style && f.fitness_racing_interest,
    },
    {
      title: 'Your Nutrition', valid: () => f.eating_style && f.dietary_preference,
    },
  ];

  const canNext = steps[step].valid();
  const isLast = step === steps.length - 1;

  const next = () => {
    if (!canNext) { toast.error('Please complete this step'); return; }
    if (isLast) return submit();
    setStep(s => s + 1);
  };
  const back = () => { if (step === 0) nav('/choose-journey'); else setStep(s => s - 1); };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: f.name, age: parseInt(f.age), gender: f.gender,
        height_cm: parseFloat(f.height_cm), current_weight_kg: parseFloat(f.current_weight_kg),
        goal: f.goal, goal_preference: needsPref ? f.goal_preference : '',
        fitness_level: f.fitness_level, training_days: parseInt(f.training_days),
        training_location: f.training_location, training_style: f.training_style,
        fitness_racing_interest: f.fitness_racing_interest || 'No',
        eating_style: f.eating_style, dietary_preference: f.dietary_preference,
        step_goal: parseInt(f.step_goal) || 8000, units: 'metric',
        ...(f.target_weight_kg ? { target_weight_kg: parseFloat(f.target_weight_kg) } : {}),
      };
      await api.post('/tracker/onboard', payload);
      await refresh();
      toast.success("You're all set! Welcome to YourReGen Free.");
      nav('/app/home');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-elev2)] text-[var(--c-ink)] px-6 py-8" data-testid="tracker-onboarding"
         style={{ paddingTop: 'max(env(safe-area-inset-top),2rem)' }}>
      <div className="max-w-md mx-auto">
        <button onClick={back} className="text-[var(--c-ink-soft)] hover:text-[var(--c-ink)] inline-flex items-center gap-2 text-sm mb-4" data-testid="onboard-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2"><RegenLogo size="sm" /><span className="label-mini text-[#7DD3FC]">YourReGen Free</span></div>

        {/* Progress */}
        <div className="flex items-center gap-1.5 mt-4" data-testid="onboard-progress">
          {steps.map((s, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#00C8FF]' : 'bg-[var(--c-film)]'}`} />
          ))}
        </div>
        <h1 className="font-display font-black text-3xl mt-4 tracking-tight">{steps[step].title}</h1>
        <p className="text-[var(--c-ink-soft)] text-xs mt-1">Step {step + 1} of {steps.length}</p>

        <div className="mt-6 space-y-4">
          {step === 0 && <StepAbout f={f} set={set} />}
          {step === 1 && <StepGoal f={f} set={set} needsPref={needsPref} />}
          {step === 2 && <StepTraining f={f} set={set} />}
          {step === 3 && <StepNutrition f={f} set={set} />}
        </div>

        <button onClick={next} disabled={busy || !canNext} data-testid="onboard-next"
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#7DD3FC] to-[#00C8FF] text-[#04203f] font-black mt-7 flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99] transition">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" />
            : isLast ? <><Check className="w-5 h-5" /> Finish Setup</>
            : <>Continue <ArrowRight className="w-5 h-5" /></>}
        </button>
      </div>
    </div>
  );
}

function Fld({ label, k, type = 'text', f, set, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={f[k]} placeholder={placeholder} onChange={e => set(k, e.target.value)} data-testid={`tk-${k}`}
             className="mt-1.5 bg-[var(--c-film)] border-[var(--c-hair)] h-11 text-[var(--c-ink)]" />
    </div>
  );
}

function Choice({ label, options, value, onSelect, testid, cols = 2, render }) {
  const COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 6: 'grid-cols-6' };
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className={`grid ${COLS[cols] || 'grid-cols-2'} gap-2 mt-1.5`}>
        {options.map(o => {
          const val = typeof o === 'object' ? o.value : o;
          const text = typeof o === 'object' ? o.label : o;
          const active = value === val;
          return (
            <button key={val} onClick={() => onSelect(val)} data-testid={`${testid}-${String(val).replace(/\s+/g, '-').toLowerCase()}`}
                    className={`h-11 rounded-xl border text-sm font-medium px-2 transition ${active ? 'border-[#00C8FF] bg-[#00C8FF]/10 text-[#00C8FF]' : 'border-[var(--c-hair)] text-[var(--c-ink-soft)] hover:border-[var(--c-hair)]'}`}>
              {render ? render(text) : text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAbout({ f, set }) {
  return (
    <>
      <Fld label="Name *" k="name" f={f} set={set} />
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Age *" k="age" type="number" f={f} set={set} />
        <div>
          <Label>Gender *</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {['Male', 'Female'].map(g => (
              <button key={g} onClick={() => set('gender', g)} data-testid={`tk-gender-${g.toLowerCase()}`}
                      className={`h-11 rounded-xl border text-sm font-medium ${f.gender === g ? 'border-[#00C8FF] bg-[#00C8FF]/10 text-[#00C8FF]' : 'border-[var(--c-hair)] text-[var(--c-ink-soft)]'}`}>{g}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Height * (cm)" k="height_cm" type="number" f={f} set={set} />
        <Fld label="Weight * (kg)" k="current_weight_kg" type="number" f={f} set={set} />
      </div>
      <Fld label="Target weight (kg)" k="target_weight_kg" type="number" f={f} set={set} placeholder="Optional" />
    </>
  );
}

function StepGoal({ f, set, needsPref }) {
  return (
    <>
      <Choice label="What's your main goal? *" options={GOALS} value={f.goal}
              onSelect={(v) => { set('goal', v); set('goal_preference', ''); }} testid="goal" cols={2} />
      {isLose(f.goal) && (
        <Choice label="How would you like to lose weight? *" options={LOSE_PREFS} value={f.goal_preference}
                onSelect={(v) => set('goal_preference', v)} testid="pref" cols={1} />
      )}
      {isMuscle(f.goal) && (
        <Choice label="What is your priority? *" options={MUSCLE_PREFS} value={f.goal_preference}
                onSelect={(v) => set('goal_preference', v)} testid="pref" cols={1} />
      )}
      {!needsPref && f.goal && (
        <p className="text-xs text-[var(--c-ink-soft)]">We'll build your plan around maintaining and improving your fitness.</p>
      )}
    </>
  );
}

function StepTraining({ f, set }) {
  return (
    <>
      <Choice label="Fitness level *" options={FITNESS} value={f.fitness_level} onSelect={(v) => set('fitness_level', v)} testid="fitness" cols={3} />
      <Choice label="Days per week you want to train *" options={DAYS} value={f.training_days ? Number(f.training_days) : ''} onSelect={(v) => set('training_days', v)} testid="days" cols={6} />
      <Choice label="Where will you train? *" options={LOCATIONS} value={f.training_location} onSelect={(v) => set('training_location', v)} testid="location" cols={3} />
      <Choice label="Preferred training style *" options={STYLES} value={f.training_style} onSelect={(v) => set('training_style', v)} testid="style" cols={3} />
      <div>
        <Choice label="Interested in Fitness Racing training? *" options={RACING} value={f.fitness_racing_interest} onSelect={(v) => set('fitness_racing_interest', v)} testid="racing" cols={2} />
        <p className="text-xs text-[var(--c-ink-soft)] mt-2 leading-relaxed">
          Fitness Racing combines running or cardio with functional strength &amp; conditioning exercises performed in circuits — designed for people preparing for fitness racing competitions or who simply enjoy this challenging hybrid training. This is independent of your main goal.
        </p>
      </div>
    </>
  );
}

function StepNutrition({ f, set }) {
  return (
    <>
      <Choice label="Eating style *" options={EATING} value={f.eating_style} onSelect={(v) => set('eating_style', v)} testid="eating" cols={1} />
      <Choice label="Dietary preference *" options={DIETARY} value={f.dietary_preference} onSelect={(v) => set('dietary_preference', v)} testid="dietary" cols={2} />
      <Fld label="Daily step goal" k="step_goal" type="number" f={f} set={set} />
    </>
  );
}
